// Hinglish educational commerce article for shop owners (dukandaar).
// Hand-built article page: needs inline citation links, figure captions, an
// offer card wired to the live campaign, and a scroll-gated sticky CTA — none
// of which the CMS block model can express.
import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ChevronDown, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { ClusterLinks } from "@/components/ClusterLinks";
import { RelatedReading } from "@/components/RelatedReading";
import { relatedArticles } from "@/lib/clusters";
import { useInauguralCampaign } from "@/lib/promo/useCampaigns";
import { formatEndDateShort } from "@/lib/promo/inaugural";
import hero1600 from "@/assets/blog/dukandaar-shaam-ki-thakaan-1600.webp.asset.json";
import hero960 from "@/assets/blog/dukandaar-shaam-ki-thakaan-960.webp.asset.json";
import hero640 from "@/assets/blog/dukandaar-shaam-ki-thakaan-640.webp.asset.json";
import offer1600 from "@/assets/blog/mag-absorb-pro-evening-bundle-1600.webp.asset.json";
import offer960 from "@/assets/blog/mag-absorb-pro-evening-bundle-960.webp.asset.json";
import offer640 from "@/assets/blog/mag-absorb-pro-evening-bundle-640.webp.asset.json";

const SLUG = "shareer-ki-thakaan-dukaandaar-magnesium";
const URL = `https://truwe.in/blog/${SLUG}`;
const TITLE = "Shareer Ki Thakaan: Dukandaar Ke Liye Guide | TruWe";
const DESCRIPTION =
  "Dukaan ke baad shareer ki thakaan aur pair mein dard? Karan, daily routine aur magnesium ka role samjhein. Mag Absorb Pro ki formulation dekhein.";
const EXCERPT =
  "Dukaan ka shutter gir jaata hai. Lekin shareer ki thakaan ghar tak saath aati hai. Samjhein kaam, rest aur magnesium ka ismein kya role hai.";
export const PUBLISHED_AT = "2026-09-07";

const NIOSH = "https://www.cdc.gov/niosh/bulletin/2019/reducing-fatigue.html";
const NHS = "https://www.nhs.uk/symptoms/tiredness-and-fatigue/";
const ODS_PRO = "https://ods.od.nih.gov/factsheets/Magnesium-HealthProfessional/";
const ODS_CONSUMER = "https://ods.od.nih.gov/factsheets/Magnesium-Consumer/";
const NCCIH =
  "https://www.nccih.nih.gov/health/sleep-disorders-and-complementary-health-approaches";

const FAQS = [
  {
    q: "Shareer ki thakaan kaise door karein?",
    a: "Pehle sleep, meals, hydration aur working routine ko dekhein. Sirf supplement par depend na karein. Persistent unexplained fatigue mein medical advice lein; treatment cause par depend karta hai.",
    source: { label: "NHS", url: NHS },
  },
  {
    q: "Kya thakan mahsus hona magnesium ki kami hai?",
    a: "Zaroori nahi. Magnesium deficiency ek possible cause hai, lekin symptoms se akela diagnosis nahi hota.",
    source: { label: "NIH ODS", url: ODS_CONSUMER },
  },
  {
    q: "Kya pair ka dard hone par Mag Absorb Pro le sakte hain?",
    a: "Pair ke dard ka cause pehle samjhein. Yeh nutritional supplement hai; painkiller ya har tarah ke leg pain ka treatment nahi.",
  },
  {
    q: "Kya magnesium supplement sirf gym jaane walon ke liye hai?",
    a: "Nahi. Magnesium ki nutritional need occupation par depend nahi karti. Supplement ki suitability individual intake, health aur medicines ke context mein decide honi chahiye.",
  },
  {
    q: "Mag Absorb Pro kitne din mein result dega?",
    a: "Har user ke liye fixed result timeline establish nahi hai. Apni response aur tolerance par dhyaan dein. Thakaan bani rahe toh bottle khatam hone ka wait karke assessment delay na karein.",
  },
  {
    q: "Kya 90-day pack lena zaroori hai?",
    a: "Nahi. Bundle ek purchase option hai. Pehli baar use kar rahe hain toh suitability aur tolerance samajhna bulk purchase se pehle aata hai.",
  },
] as const;

const TOC = [
  { id: "thakan-ke-karan", label: "Thakan hone ke karan" },
  { id: "pair-mein-dard", label: "Pairo mein dard" },
  { id: "routine", label: "Routine mein kya badlein" },
  { id: "magnesium-ke-fayde", label: "Magnesium ke fayde" },
  { id: "mag-absorb-pro", label: "Mag Absorb Pro ka role" },
  { id: "safety", label: "MAgnesium Lene se pehle kya Dekhna Chahiye" },
  { id: "offer", label: "Pack options" },
  { id: "faq", label: "Aapke Khuch sawaal hamare jawab" },
] as const;

export const Route = createFileRoute("/blog/shareer-ki-thakaan-dukaandaar-magnesium")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      ogTitle: "Shareer ki thakaan: dukaan ke baad apna khayal kaise rakhein?",
      ogDescription: EXCERPT,
      headline: "Shareer ki thakaan: dukaan ke baad apna khayal kaise rakhein?",
      breadcrumbName: "Shareer ki thakaan (dukandaar guide)",
      datePublished: PUBLISHED_AT,
      dateModified: PUBLISHED_AT,
      articleType: "BlogPosting",
      authorName: "TruWe Editorial",
      ogImage: `https://truwe.in${hero1600.url}`,
      faqs: FAQS.map((f) => ({ q: f.q, a: f.a })),
    }),
});

/** Consent-aware: GTM owns GA4/Meta and enforces Consent Mode v2. */
function trackCtaClick(placement: "inline" | "final" | "sticky") {
  if (typeof window === "undefined") return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "article_product_cta_click",
    article_slug: SLUG,
    cta_placement: placement,
    product_handle: "mag-absorb-pro",
  });
}

function Src({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener nofollow"
      className="text-brand underline underline-offset-2"
    >
      {label}
    </a>
  );
}

function Figure({
  wide,
  mid,
  small,
  alt,
  caption,
  eager,
}: {
  wide: string;
  mid: string;
  small: string;
  alt: string;
  caption: string;
  eager?: boolean;
}) {
  return (
    <figure className="mt-8">
      <img
        src={mid}
        srcSet={`${small} 640w, ${mid} 960w, ${wide} 1600w`}
        sizes="(min-width: 768px) 720px, 100vw"
        alt={alt}
        width={1672}
        height={941}
        loading={eager ? "eager" : "lazy"}
        // eslint-disable-next-line react/no-unknown-property
        fetchPriority={eager ? "high" : "auto"}
        decoding="async"
        className="h-auto w-full rounded-3xl border border-border object-contain"
      />
      <figcaption className="mt-2 text-xs text-muted-foreground">{caption}</figcaption>
    </figure>
  );
}

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2
      id={id}
      className="mt-12 scroll-mt-28 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl"
    >
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-4 text-[17px] leading-8 text-foreground/85 md:text-[18px]">{children}</p>;
}

function OfferCard({ onCta }: { onCta: () => void }) {
  // Authoritative offer state — never invented here.
  const campaign = useInauguralCampaign("mag-absorb-pro");
  const bundleLive = !!campaign && campaign.qualifyingQty === 3;

  return (
    <section
      id="offer"
      className="mt-14 scroll-mt-28 overflow-hidden rounded-3xl border border-brand/20 bg-brand/[0.04]"
    >
      <Figure
        wide={offer1600.url}
        mid={offer960.url}
        small={offer640.url}
        alt="Ghar ki side table par Mag Absorb Pro ki teen bottles, paani ka glass, diary aur dukaan ki chaabiyan."
        caption="Mag Absorb Pro ka illustrative lifestyle visual; pack ki poori jaankari product page par dekhein."
      />
      <div className="p-6 md:p-8">
        {bundleLive ? (
          <>
            <p className="inline-flex items-center rounded-full border border-brand/30 bg-brand/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-deep">
              Limited time offer
            </p>
            <h3 className="mt-3 font-display text-xl font-extrabold text-brand-deep md:text-2xl">
              Mag Absorb Pro — BUY 2, GET 1 FREE
            </h3>
            <p className="mt-3 text-[17px] font-semibold leading-8 text-brand-deep">
              Do bottles khareediye. Teesri humari taraf se.{" "}
              <span className="font-normal">
                Yeh limited-period offer hai
                {campaign ? `, ${formatEndDateShort(campaign)} tak` : ""} — permanent price nahi.
              </span>
            </p>
            <P>
              Suitable daily routine ke liye 3-bottle pack explore kijiye. Listed serving ke
              hisaab se 90-day supply; yeh treatment course ya result ki guarantee nahi hai.
            </P>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/products/$handle"
                params={{ handle: "mag-absorb-pro" }}
                search={{ pack: 3 as const }}
                onClick={onCta}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-deep"
              >
                3-bottle offer dekhein <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/products/$handle"
                params={{ handle: "mag-absorb-pro" }}
                search={{ pack: 1 as const }}
                onClick={onCta}
                className="inline-flex items-center justify-center rounded-full border border-brand/30 px-6 py-3 text-sm font-semibold text-brand-deep transition hover:bg-brand/10"
              >
                Pehle single bottle aur formulation dekhein
              </Link>
            </div>
            <p className="mt-4 text-xs italic text-muted-foreground">
              Offer ki current availability aur terms product page par dekhein. Supplement
              suitability pehle confirm karein.
            </p>
          </>
        ) : (
          <>
            <h3 className="font-display text-xl font-extrabold text-brand-deep md:text-2xl">
              Apni routine ke liye Mag Absorb Pro explore karein
            </h3>
            <P>Ingredients, pack details aur current availability ek jagah dekhein.</P>
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              onClick={onCta}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-deep"
            >
              Formulation aur options dekhein <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>
    </section>
  );
}

function Article() {
  const productRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const [reachedProduct, setReachedProduct] = useState(false);
  const [nearEnd, setNearEnd] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === productRef.current && e.isIntersecting) setReachedProduct(true);
        if (e.target === endRef.current) setNearEnd(e.isIntersecting);
      }
    });
    if (productRef.current) io.observe(productRef.current);
    if (endRef.current) io.observe(endRef.current);
    return () => io.disconnect();
  }, []);

  const showSticky = reachedProduct && !nearEnd;

  return (
    <article
      lang="hi-Latn"
      className="bg-background pb-28 pt-28 md:pt-36"
      data-article-slug={SLUG}
    >
      <div className="mx-auto max-w-[720px] px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Sleep &amp; Recovery
          </p>
          <h1 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Shareer ki thakaan: dukaan ke baad apna khayal kaise rakhein?
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="font-medium text-brand-deep">By TruWe Editorial</span>
            <span aria-hidden>·</span>
            <time dateTime={PUBLISHED_AT}>7 September 2026</time>
            <span aria-hidden>·</span>
            <span className="rounded-full bg-brand/10 px-2 py-0.5 font-semibold text-brand">
              TruWe educational product guide
            </span>
          </div>
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>

          <p className="mt-6 text-[17px] font-semibold leading-8 text-brand-deep md:text-[18px]">
            Dukaan band ho gayi. Shareer ki thakaan abhi baaki hai.
          </p>
          <P>Counter saaf. Hisaab ki diary band. Chaabi jeb mein.</P>
          <P>
            Ghar pahunchkar joote utaarte hain, toh bas kuch der baithne ka mann karta hai.
            Kisi se baat karne se pehle shareer ko thoda aaram chahiye.
          </P>
          <P>
            Agar aap khud dukaan sambhalte hain, toh yeh scene jaana-pehchaana lag sakta hai.
            Customer ko samaan dikhana, shelf tak jaana, parcel uthana, phir counter par
            lautna. Din mein alag se workout na bhi ho, kaam shareer se hota rehta hai.
          </P>
          <P>
            <strong>
              Shareer ki thakaan ko samajhne ki shuruaat aapke poore din se hoti hai:
            </strong>{" "}
            kaam kitna tha, khana kab khaya, paani kitna piya aur rest ke liye kitna waqt
            mila.
          </P>
          <P>
            Is guide mein thakan hone ke Wajah, chhote practical changes aur magnesium
            supplement ki sahi jagah samjhenge. Saath mein TruWe ke Mag Absorb Pro ki
            formulation bhi dekhenge, taaki aap informed decision le sake.
          </P>

          <Figure
            eager
            wide={hero1600.url}
            mid={hero960.url}
            small={hero640.url}
            alt="Shaam ko kapdon ki dukaan ke counter ke paas baitha dukandaar, saath mein hisaab ki diary aur chaabiyan."
            caption="Dukaan ke baad apne liye bhi waqt rakhiye. AI-generated illustrative image."
          />

          <nav
            aria-label="Is page mein"
            className="mt-8 rounded-2xl border border-border bg-card p-4"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              Is page mein
            </p>
            <ul className="mt-2 grid gap-1 sm:grid-cols-2">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className="text-sm text-muted-foreground underline-offset-2 hover:text-brand hover:underline"
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="thakan-ke-karan">Dukandaar ko thakan hone ke karan kya ho sakte hain?</H2>
          <P>
            Kabhi lunch rakhte hi customer aa jaata hai. Kabhi baithne ke liye rakhi kursi par
            bhi carton pade rehte hain. Apne liye socha hua break aage khisakta rehta hai.
          </P>
          <P>
            Bohot Lambe working hours, Bohot der tak khade rehna aur recovery ke liye kam waqt retail work
            mein fatigue ke factors hain. NIOSH ki retail guidance bhi inhe highlight karti
            hai. Yeh international workplace evidence hai; har Indian dukandaar ki health ka
            diagnosis nahi. <Src label="NIOSH: retail fatigue" url={NIOSH} />
          </P>
          <P>
            Thakan mahsus hona poor sleep, diet, stress ya kisi medical condition se bhi juda
            ho sakta hai. Roz ki unexplained thakaan ko sirf kaam ka naam dekar taalna theek
            nahi. <Src label="NHS: tiredness and fatigue" url={NHS} />
          </P>
          <P>Apne aap se teen sawaal poochhiye:</P>
          <ul className="mt-3 space-y-2 text-[17px] leading-8 text-foreground/85">
            <li>
              • Kya aaj mujhe kaam ke beech baithne aur position badalne ka mauka mila?
            </li>
            <li>• Kya maine proper meal liya, ya bas jo jaldi mila usi se din nikala?</li>
            <li>• Kya main sone ke liye waqt nikaal raha hoon, ya bachi hui raat mein so raha hoon?</li>
          </ul>
          <P>
            Jawab likhne ki zaroorat nahi. Apne din ko ek baar dhyaan se dekhna zaroori hai.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="pair-mein-dard">Din bhar khade rehne ke baad pair mein dard hota hai?</H2>
          <P>
            “Pair ka dard” bolte waqt har insaan ek jaisi takleef describe nahi karta. Kisi ko
            heaviness lagti hai, kisi ko talwe mein discomfort, kisi ko cramp. Isliye body
            pain after work ka ek hi solution maan lena jaldi hogi.
          </P>
          <P>
            Counter ke paas baithne ki jagah, kaam ke beech position badalna, regular rest
            breaks aur suitable footwear ya standing mat par dhyaan dena useful starting
            points hain. NIOSH bhi standing retail work ke liye aise workplace changes suggest
            karta hai. <Src label="NIOSH guidance" url={NIOSH} />
          </P>
          <P>
            <strong>Mag Absorb Pro ko pair mein dard ki dawa samajhkar mat chuniye.</strong>{" "}
            Dard baar-baar ho, badh raha ho ya chalne mein dikkat kare, toh uska cause assess
            karwana zaroori hai.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="routine">Shareer ki thakaan ke liye routine mein kya badlein?</H2>
          <P>
            Ek complicated wellness routine dukaan ke schedule mein fit karna mushkil ho sakta
            hai. Shuruaat in chaar cheezon se kijiye:
          </P>
          <P>
            <strong>1. Break ke liye jagah banaiye.</strong> Kursi available rakhiye. Jab
            mauka mile, ek hi position mein rehne ke bajaye baithiye ya halka move kijiye.
          </P>
          <P>
            <strong>2. Paani aur meal ko kaam ke aas-paas plan kijiye.</strong> Paani paas
            rakhiye; lunch ka practical slot tay kijiye. Busy din ke liye ghar se ek simple
            meal ya snack rakhna aasaan pad sakta hai.
          </P>
          <P>
            <strong>3. Sone ka waqt bachaiye.</strong> Adults ko aam taur par kam se kam saat
            ghante ki neend ka mauka chahiye. Phone aur dukaan ke pending kaam ke liye roz
            wahi waqt kaatna recovery ko mushkil bana sakta hai.
          </P>
          <P>
            <strong>4. Raat ko caffeine aur screen par dhyaan dijiye.</strong> Sone ke kareeb
            chai aur scrolling kam karke dekhiye. Regular bedtime aur shaant sleeping space
            banaiye. <Src label="NIOSH" url={NIOSH} />, <Src label="NHS sleep habits" url={NHS} />
          </P>
          <P>Aaj sab kuch badalna zaroori nahi. Ek cheez chuniye jise kal bhi repeat kar paayein.</P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="magnesium-ke-fayde">
            Magnesium ke fayde: muscles aur energy mein kya role hai?
          </H2>
          <P>
            Magnesium normal muscle aur nerve function mein kaam karta hai. Energy production
            ke processes mein bhi iska role hai. Yeh physiological role hai; extra magnesium
            se har insaan ki energy badhne ka proof nahi.{" "}
            <Src label="NIH ODS: magnesium" url={ODS_PRO} />
          </P>
          <P>
            Magnesium ki kami mein fatigue aur weakness ho sakti hai, lekin{" "}
            <strong>thakaan akeli deficiency confirm nahi karti</strong>. Dietary intake kam ho
            toh suitable supplementation intake ko support kar sakti hai. Dal, nuts, seeds,
            whole grains aur hari sabziyaan bhi magnesium ke sources hain.{" "}
            <Src label="NIH ODS consumer guide" url={ODS_CONSUMER} />
          </P>
          <h3 className="mt-8 font-display text-xl font-bold text-brand-deep md:text-2xl">
            Kya magnesium se chain ki neend aayegi?
          </h3>
          <P>
            Magnesium aur sleep par research limited aur mixed hai. Kuch studies mein benefit
            mila hai, lekin pakki neend, insomnia treatment ya agle din guaranteed freshness
            ka promise evidence se support nahi hota.{" "}
            <Src label="NCCIH: magnesium and sleep" url={NCCIH} />
          </P>
          <P>
            Isliye evening routine banate waqt sleep timing, work breaks aur nutrition ko
            saath rakhiye.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <div ref={productRef}>
            <H2 id="mag-absorb-pro">Mag Absorb Pro is routine mein kaise fit hota hai?</H2>
            <P>
              Aap magnesium supplement consider kar rahe hain, toh formulation padhne se
              shuruaat kijiye.
            </P>
            <P>
              <strong>TruWe Mag Absorb Pro ek 5-form magnesium complex hai.</strong> Ismein
              Liposomal Magnesium Bisglycinate, Aquamin® Marine Magnesium, Magnesium Chloride,
              Magnesium Aspartate aur Magnesium Gluconate listed hain. Saath mein Zinc, Copper,
              P-5-P Vitamin B6 aur Vitamin D3 cofactors hain.{" "}
              <Link
                to="/products/$handle"
                params={{ handle: "mag-absorb-pro" }}
                className="text-brand underline underline-offset-2"
              >
                TruWe formulation
              </Link>
            </P>
            <P>
              Iska practical purpose magnesium intake ko support karne ka ek option dena hai.
              Magnesium ka normal muscle, nerve aur energy metabolism mein role isi nutritional
              rationale ka hissa hai. Kisi specific dukandaar ki thakaan kitni kam hogi, iska
              finished-product result hum promise nahi kar rahe.
            </P>
            <P>
              <strong>5 forms ka matlab 5 guna result nahi hota.</strong> Aapke liye total
              elemental magnesium, suitability, tolerance aur label directions bhi matter
              karte hain.
            </P>
            <P>
              Product page ke mutabik ek bottle mein <strong>60 tablets, yani 30 servings</strong>{" "}
              hain; listed serving do tablets hai. Current pack ki directions aur apne
              clinician ki advice follow kijiye.{" "}
              <Link
                to="/products/$handle"
                params={{ handle: "mag-absorb-pro" }}
                className="text-brand underline underline-offset-2"
              >
                Mag Absorb Pro product details
              </Link>
            </P>
            <P>
              <strong>Formulation samajhkar apni routine ka agla step chuniye.</strong>
            </P>
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              onClick={() => trackCtaClick("inline")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition hover:bg-brand-deep"
            >
              Mag Absorb Pro ki formulation dekhein <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="safety">Magnesium supplement lene se pehle kya dekhein?</H2>
          <div className="mt-4 rounded-2xl border border-border bg-card p-5">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              <ShieldCheck className="h-3.5 w-3.5" /> Safety
            </p>
            <P>
              Label par elemental magnesium aur recommended serving check kijiye. Compound ka
              total weight elemental magnesium ke barabar nahi hota.
            </P>
            <P>
              Kidney disease, pregnancy, breastfeeding ya regular medicines ke case mein
              clinician se suitability poochhiye. Magnesium kuch medicines, including certain
              antibiotics, ke absorption ko affect kar sakta hai; higher supplemental intake se
              loose stools, nausea ya cramps ho sakte hain. <Src label="NIH ODS" url={ODS_PRO} />
            </P>
            <P>
              <strong>
                Agar thakaan kuch hafton se chal rahi hai, daily life affect kar rahi hai ya
                saath mein doosre symptoms hain, toh medical assessment karaiye.
              </strong>{" "}
              Supplement khareedne se pehle cause samajhna zyada useful ho sakta hai.{" "}
              <Src label="NHS" url={NHS} />
            </P>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <H2 id="apne-liye">
            Aap dukaan ke liye waqt nikaalte hain. Apne liye bhi rakhiye.
          </H2>
          <P>
            Dukaan ki har chhoti zaroorat nazar aa jaati hai. Kaunsa stock khatam ho raha hai.
            Kis shelf ko saaf karna hai. Kis customer ko call karna hai.
          </P>
          <P>Apne shareer ki zarooraton ko bhi us list mein jagah dijiye.</P>
          <P>
            Aaj ka step ek proper lunch ho sakta hai. Kal counter ke paas khaali kursi. Aur
            agar magnesium supplementation aapke liye suitable hai, toh ek aisi formulation
            jiske ingredients aap samajhte hon.
          </P>
        </Reveal>

        <div ref={endRef}>
          <OfferCard onCta={() => trackCtaClick("final")} />
        </div>

        <Reveal delay={0.05}>
          <h2
            id="faq"
            className="mt-14 scroll-mt-28 font-display text-2xl font-extrabold text-brand-deep md:text-3xl"
          >
            Aapke sawaal
          </h2>
          <div className="mt-4 space-y-3">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl border border-border bg-card p-5"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-display text-base font-bold text-brand-deep">
                  {f.q}
                  <ChevronDown
                    aria-hidden
                    className="h-4 w-4 shrink-0 text-brand transition group-open:rotate-180"
                  />
                </summary>
                <p className="mt-2 text-[16px] leading-7 text-muted-foreground">
                  {f.a}{" "}
                  {"source" in f && f.source ? (
                    <Src label={f.source.label} url={f.source.url} />
                  ) : null}
                </p>
              </details>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClusterLinks cluster="magnesium" />
          <RelatedReading articles={relatedArticles(SLUG)} />
          <p className="mt-12 text-xs italic leading-6 text-muted-foreground">
            Yeh TruWe ki educational product guide hai, personal medical advice nahi. Mag
            Absorb Pro ek nutraceutical hai; balanced diet ya medical treatment ka substitute
            nahi. Images illustrative hain, customer testimonials nahi.
          </p>
        </Reveal>
      </div>

      {/* Mobile sticky CTA — only between the product section and the offer card. */}
      <div
        style={{ bottom: "calc(max(0.75rem, env(safe-area-inset-bottom)) + 4.75rem)" }}
        className={`fixed inset-x-0 z-30 px-4 transition-transform md:hidden ${
          showSticky ? "translate-y-0" : "pointer-events-none translate-y-full"
        }`}
        aria-hidden={!showSticky}
      >
        <Link
          to="/products/$handle"
          params={{ handle: "mag-absorb-pro" }}
          onClick={() => trackCtaClick("sticky")}
          tabIndex={showSticky ? 0 : -1}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white"
        >
          Mag Absorb Pro ki formulation dekhein <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}
